/**
 * The control sheet's head readings for the hosts that are NOT the modal — the
 * dashboard `device-control` card and the room page's full-control card (card
 * t_488df70b, P2 of the control-sheet redesign).
 *
 * The modal keeps its own live-subscribed `deviceStatusColor` computed (a
 * literal colour off the socket values, fed to the chrome as `accent`); these
 * two hosts hold the device row they were rendered with, so everything here
 * reads that row and nothing else: no subscription, no request, and nothing
 * that can throw — a throw inside a host's render is a blank card, which lint,
 * types and the unit suite all walk past (rule 16).
 *
 * `deviceSheetCategory` is the one place the category fold lives: the modal's
 * computeds read it too, so the head's colour, the pill's tone and the pill's
 * text cannot disagree about what kind of device they are looking at.
 */
import type { Device } from '../types';
import { tt } from './consoleText';
import {
  ROBOT_BATTERY_KEY,
  robotAway,
  robotKindOf,
  robotPercent,
  robotState,
  robotStateWord,
} from './robotState';

/** One device value row — the app's rows carry `valueKey`; the raw API spelling is read too. */
type SheetValueRow = {
  valueKey?: string;
  property_key?: string;
  currentValue?: unknown;
  unit?: string;
};

/** The device as the head reads it: the type spellings plus the values bag. */
type SheetDevice = Device & {
  deviceValues?: SheetValueRow[];
  controlType?: string;
  values?: SheetValueRow[];
};

/** The device's value rows, either spelling the row arrived under. */
function sheetValues(device: SheetDevice): SheetValueRow[] {
  return device.deviceValues ?? device.values ?? [];
}

/** The first of `keys` the row carries — the live/static read the modal also uses. */
function sheetRead(device: SheetDevice, keys: string[]): unknown {
  for (const key of keys) {
    const row = sheetValues(device).find((value) => String(value.valueKey ?? '') === key);
    if (row !== undefined) return row.currentValue;
  }
  return undefined;
}

/** The on/off reading a boolean datapoint gives, in any of the spellings the hub uses. */
function sheetOn(device: SheetDevice, keys: string[]): boolean {
  const value = sheetRead(device, keys);
  return value === true || value === 1 || value === 'on';
}

/** The off reading — the reverse of `sheetOn`, for the flags where an explicit
 *  `false` is a reading and a missing key is not (a Synology disk's `smart_ok`). */
function sheetOff(device: SheetDevice, keys: string[]): boolean {
  const value = sheetRead(device, keys);
  return value === false || value === 0 || value === 'false' || value === 'off';
}

/**
 * The device's category — mirrors `DeviceControlModal.deviceStatusColor`'s own
 * fold: `light_*` → light, `shade_*` → cover, else the raw type (or `generic`).
 *
 * The two robots come first (card t_390bc438): both of their classes carry
 * `controlType: "switch"`, so the fold below would file a robot vacuum as a
 * switch and the head's pill would read On/Off for a machine that is docked,
 * cleaning or returning. Their own type names them (`robotKindOf`).
 */
export function deviceSheetCategory(device: Device): string {
  const row = device as SheetDevice;
  const robot = robotKindOf(row);
  if (robot) return robot;

  // The heat-demand controller's own family (owner, 22.10.2026). It carries no
  // setpoint and no `power`/`state` row — its readable state is the boiler
  // contact (`heat_demand`) — so the `switch` fold below would colour nothing
  // and the head's pill would read "Off" whatever the boiler is doing.
  if (String(row.deviceType ?? '').toLowerCase() === 'heat_demand_controller') return 'heat';

  const rawType = (row.deviceControl || row.controlType || row.deviceType || '').toLowerCase();
  if (rawType.startsWith('light_')) return 'light';
  if (rawType.startsWith('shade_')) return 'cover';
  return rawType || 'generic';
}

/**
 * The Synology family's kind — the same fold its own body reads
 * (SynologyWidget's `kind`, off the type naming it). One device class per kind
 * and each kind carries its own health values, so the head's folds must decide
 * which one they are looking at before they can read either.
 */
export function deviceSheetSynologyKind(device: Device): 'nas' | 'volume' | 'disk' {
  const row = device as SheetDevice;
  const type = String(row.deviceType || row.controlType || '').toLowerCase();
  if (type.includes('volume')) return 'volume';
  if (type.includes('disk')) return 'disk';
  return 'nas';
}

/**
 * The `--device-color-*` token a category's head tints with, so a card's head
 * reads the same colour language the tiles and the modal use. A category
 * without a token keeps the chrome's own default (`--device-color-light`).
 */
export function deviceSheetAccent(device: Device): string | undefined {
  const category = deviceSheetCategory(device);
  switch (category) {
    case 'light':
      return 'var(--device-color-light)';
    case 'switch':
      return 'var(--device-color-switch)';
    case 'cover':
      return 'var(--device-color-cover)';
    case 'lock':
      return 'var(--device-color-lock)';
    case 'thermostat':
    case 'climate':
      return 'var(--device-color-thermostat)';
    case 'fan':
      return 'var(--device-color-fan)';
    case 'alarm':
      return 'var(--device-color-alarm)';
    case 'binary_sensor':
      return 'var(--device-color-binary-sensor)';
    case 'strike_lock':
      return 'var(--device-color-strike-lock)';
    case 'presence':
      // The tracked client (P10's `presence` family): the body's own present
      // tone, so head and tile read the same green.
      return 'var(--device-color-switch)';
    case 'synology':
      // The storage family's healthy tone — the one its body's meters ride.
      return 'var(--device-color-switch)';
    case 'vacuum':
      return 'var(--device-color-vacuum)';
    case 'lawn_mower':
      return 'var(--device-color-mower)';
    case 'heat':
      return 'var(--device-color-heat)';
    default:
      return undefined;
  }
}

/**
 * The room-view contacts: occupancy sensors (the "… Motion" devices), whose
 * thumbnail is a picture of the room they watch rather than of the device itself.
 * Owner, 24.10.2026: "it should be black end white. and on motion we turn it in
 * color", plus a breathing card while it detects.
 *
 * `presence_sensor` is deliberately NOT here: those rows carry device photos from
 * the old config, and greying a picture of a sensor reads as a broken tile, not as
 * "the room is still". Give them their own treatment (or room photos) first.
 */
export function deviceIsMotionSensor(device: Device): boolean {
  return String((device as SheetDevice).deviceType ?? '').toLowerCase() === 'occupancy_sensor';
}

/**
 * Is the device doing something right now — the head circle's tint and, where a
 * host passes one, its pill's tone. Every case is the category's own "has its
 * colour" state, matching what the modal's `deviceStatusColor` returns a colour
 * for: a light that is on, a cover off its stop, a locked lock, an unlocked
 * strike lock, an armed alarm, a lit sensor, a set thermostat, a present
 * client, a healthy storage unit.
 */
export function deviceSheetActive(device: Device): boolean {
  const row = device as SheetDevice;
  switch (deviceSheetCategory(device)) {
    case 'light':
      return sheetOn(row, ['power', 'on_off', 'state']);
    case 'switch':
      return sheetOn(row, ['power', 'on_off', 'state', 'switch', 'enabled']);
    case 'fan': {
      // The fan's own fold (FanWidget's `fanMode`/`isOn`): a speed-family fan
      // (`fan_speed`, speed_bool/speed_enum — the Toilet / Bathroom fans)
      // carries no power key, so its running reading IS `fan_speed > 0`; the
      // continuous / on_off families keep the power keys (card t_2393f4f0).
      const carriesSpeed = sheetValues(row).some((value) => String(value.valueKey ?? '') === 'fan_speed');
      if (carriesSpeed) {
        const speed = Number(sheetRead(row, ['fan_speed']));
        return Number.isFinite(speed) && speed > 0;
      }
      return sheetOn(row, ['switch', 'power', 'on_off', 'state']);
    }
    case 'cover': {
      const position = Number(sheetRead(row, ['position_status', 'position', 'level']));
      return Number.isFinite(position) && position > 0;
    }
    case 'lock': {
      const locked = sheetRead(row, ['locked']);
      return locked === true || locked === 1 || sheetOn(row, ['on_off', 'state']);
    }
    case 'strike_lock': {
      const unlock = sheetRead(row, ['unlock']);
      return unlock === true || unlock === 1 || sheetOn(row, ['power', 'on_off', 'state']);
    }
    case 'alarm': {
      const state = String(sheetRead(row, ['alarm_state', 'arm_state', 'state']) ?? '');
      return state.startsWith('armed') || state === 'triggered';
    }
    case 'binary_sensor':
      return sheetOn(row, ['state', 'power', 'on_off', 'occupancy', 'sensor_value']);
    case 'presence_sensor':
      return sheetOn(row, ['presence', 'zone_1_presence', 'state']);
    case 'thermostat':
    case 'climate': {
      const target = sheetRead(row, ['effective_target', 'target_temperature', 'setpoint']);
      return target !== undefined && target !== null && target !== '';
    }
    case 'computer':
      return sheetOn(row, ['online']);
    case 'presence':
      // The tracked UniFi client's own `present` (its body's tile), with the
      // presence_sensor spelling accepted for a row that carries it.
      return sheetOn(row, ['present', 'presence', 'zone_1_presence']);
    case 'synology': {
      // The family's own health fold (SynologyWidget's): the NAS its `online`,
      // a volume its `volume_error`, a disk its `disk_error`/`smart_ok` (the
      // driver mirrors the two — either spelling reports the same failure).
      // An errored unit reads inactive here: these embedded hosts hold the row
      // they rendered with and carry no state colour — the modal, which
      // subscribes, reads it alarm.
      const kind = deviceSheetSynologyKind(device);
      if (kind === 'nas') return sheetOn(row, ['online']);
      if (kind === 'volume') return !sheetOn(row, ['volume_error']);
      return !sheetOn(row, ['disk_error']) && !sheetOff(row, ['smart_ok']);
    }
    case 'vacuum':
    case 'lawn_mower':
      // The robots' own fold (card t_390bc438): the RVC state decides. Away
      // from the dock — working, paused on the job, or on its way home — reads
      // active; docked and charging are rest. A device that has reported no
      // state reads inactive, never "on".
      return robotAway(robotState(sheetValues(row)));
    case 'heat':
      // The boiler contact, not the master switch: the switch being on is not
      // the boiler running.
      return sheetOn(row, ['heat_demand']);
    default:
      return sheetOn(row, ['power', 'on_off', 'state', 'switch', 'occupancy', 'online']);
  }
}

/** A numeric reading, or `null` when the row is missing / not a number. */
function sheetNumber(row: SheetDevice, keys: string[]): number | null {
  const value = sheetRead(row, keys);
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * A device's current state in words — the pill the control sheet's head shows
 * (`DeviceControlModal.stateText`), folded from the row's own values so a list
 * host can render it without a subscription. Empty when the row carries no
 * reading the fold knows: a host paints nothing rather than a guess.
 */
export function deviceSheetStateText(device: Device): string {
  const row = device as SheetDevice;
  const on = tt('On', 'Vklopljeno');
  const off = tt('Off', 'Izklopljeno');

  switch (deviceSheetCategory(device)) {
    case 'light': {
      if (!sheetOn(row, ['power', 'on_off', 'state'])) return off;
      const brightness = sheetNumber(row, ['brightness', 'dimming', 'level']);
      return brightness === null ? on : `${on} · ${Math.round(brightness)} %`;
    }
    case 'switch':
      return sheetOn(row, ['power', 'on_off', 'state', 'switch', 'enabled']) ? on : off;
    case 'heat': {
      // The pill names the contact this device exists for, not the switch.
      const demand = sheetOn(row, ['heat_demand']);
      return demand
        ? tt('Heat demand', 'Potreba po toploti')
        : tt('No demand', 'Brez potrebe');
    }
    case 'cover': {
      // The hub's own position datapoint, the reading its cover tile shows.
      const position = sheetNumber(row, ['position_status', 'position', 'level']);
      return position === null ? '' : `${Math.round(position)} %`;
    }
    case 'thermostat':
    case 'climate': {
      const target = sheetNumber(row, ['effective_target', 'target_temperature', 'setpoint']);
      return target === null ? '' : `${Math.round(target * 10) / 10} °C`;
    }
    case 'lock': {
      const locked = sheetRead(row, ['locked']);
      const engaged =
        locked === undefined ? sheetOn(row, ['power', 'on_off', 'state']) : locked === true || locked === 1 || locked === 'locked';
      return engaged ? tt('Locked', 'Zaklenjeno') : tt('Unlocked', 'Odklenjeno');
    }
    case 'strike_lock': {
      const unlock = sheetRead(row, ['unlock']);
      const open =
        unlock === undefined
          ? sheetOn(row, ['unlock', 'power', 'on_off', 'state'])
          : unlock === true || unlock === 1;
      return open ? tt('Unlocked', 'Odklenjeno') : tt('Locked', 'Zaklenjeno');
    }
    case 'fan': {
      const carriesSpeed = sheetRead(row, ['fan_speed']) !== undefined;
      if (carriesSpeed) {
        const speed = sheetNumber(row, ['fan_speed']);
        if (speed === null || speed <= 0) return off;
        const percent = sheetNumber(row, ['fan_speed_percentage']);
        return percent !== null && percent > 0 ? `${on} · ${Math.round(percent)} %` : on;
      }
      return sheetOn(row, ['switch', 'power', 'on_off', 'state']) ? on : off;
    }
    case 'media':
    case 'media_player': {
      const playState = String(sheetRead(row, ['play_state']) ?? '').toLowerCase();
      if (playState === 'playing') return tt('Playing', 'Predvajanje');
      if (playState === 'paused') return tt('Paused', 'Premor');
      return sheetOn(row, ['power', 'on_off', 'state']) ? on : off;
    }
    case 'presence_sensor':
      return sheetOn(row, ['presence', 'zone_1_presence', 'state'])
        ? tt('Present', 'Prisoten')
        : tt('Clear', 'Prosto');
    case 'binary_sensor':
      return sheetOn(row, ['state', 'power', 'on_off', 'occupancy', 'sensor_value']) ? tt('Active', 'Aktivno') : off;
    case 'alarm': {
      const state = String(sheetRead(row, ['alarm_state', 'arm_state', 'state']) ?? '');
      if (state === 'triggered') return tt('Triggered', 'Sprožen');
      return state.startsWith('armed') ? tt('Armed', 'Aktiviran') : off;
    }
    case 'computer':
      return sheetOn(row, ['online']) ? on : off;
    case 'presence':
      return sheetOn(row, ['present', 'presence', 'zone_1_presence'])
        ? tt('Present', 'Prisoten')
        : tt('Clear', 'Prosto');
    case 'synology': {
      const kind = deviceSheetSynologyKind(device);
      if (kind === 'nas') return sheetOn(row, ['online']) ? on : off;
      if (kind === 'volume') {
        if (sheetOn(row, ['volume_error'])) return tt('Volume error', 'Napaka nosilca');
        if (sheetOn(row, ['disk_full'])) return tt('Almost full', 'Skoraj poln');
        return tt('OK', 'OK');
      }
      return sheetOn(row, ['disk_error']) || sheetOff(row, ['smart_ok'])
        ? tt('Disk error', 'Napaka diska')
        : tt('OK', 'OK');
    }
    case 'vacuum':
    case 'lawn_mower': {
      const pair = robotStateWord(robotState(sheetValues(row)));
      const word = pair ? tt(pair.en, pair.sl) : '';
      const battery = robotPercent(sheetValues(row), {}, ROBOT_BATTERY_KEY);
      if (!word) return battery === null ? '' : `${battery} %`;
      return battery === null ? word : `${word} · ${battery} %`;
    }
    default: {
      if (sheetOn(row, ['power', 'on_off', 'state', 'switch', 'occupancy', 'online'])) return on;
      for (const value of sheetValues(row)) {
        if (value.currentValue === undefined || value.currentValue === null || value.currentValue === '') continue;
        if (typeof value.currentValue === 'boolean') return value.currentValue ? on : off;
        if (typeof value.currentValue === 'number') {
          return value.unit ? `${value.currentValue} ${value.unit}` : String(value.currentValue);
        }
        if (typeof value.currentValue === 'string') return value.currentValue;
      }
      return '';
    }
  }
}
